// <fiee-chat> runs the kiosk conversation (/panel/chatbot).
//
// The visitor's message appears the moment it is sent, the assistant answers
// with a "typing" bubble (three dots, as in a messaging app), and the answer
// is written into it word by word as the model produces it. The server streams
// it as server-sent events over the POST response (see
// internal/handler/chat_stream.go); htmx 4 has no streaming swaps, so this
// island does the request itself.
//
// It handles the composer form and every [data-ask] button (suggested
// questions and topic chips). Bubbles are cloned from the server-rendered
// <template data-template="user|bot">, so their markup stays in templ.
export class FieeChat extends HTMLElement {
  #busy = false;
  #onSubmit = (e: Event) => this.#submit(e);
  #onClick = (e: Event) => this.#click(e);

  connectedCallback() {
    this.addEventListener("submit", this.#onSubmit);
    this.addEventListener("click", this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener("submit", this.#onSubmit);
    this.removeEventListener("click", this.#onClick);
  }

  get #thread() {
    return this.querySelector<HTMLElement>("[data-thread]");
  }

  get #form() {
    return this.querySelector<HTMLFormElement>("form[data-composer]");
  }

  #submit(e: Event) {
    const form = e.target as HTMLFormElement;
    if (!form.matches("[data-composer]")) return;
    e.preventDefault();
    const field = form.elements.namedItem("pregunta") as HTMLInputElement | null;
    const question = field?.value.trim() ?? "";
    if (question) void this.ask(question);
  }

  #click(e: Event) {
    const button = (e.target as Element).closest<HTMLButtonElement>("button[data-ask]");
    if (!button || button.disabled) return;
    void this.ask(button.dataset["ask"] ?? "");
  }

  async ask(question: string) {
    const thread = this.#thread;
    if (this.#busy || !question || !thread) return;
    this.#setBusy(true);

    thread.append(this.#bubble("user", question, clock()));
    this.#form?.reset();
    const reply = this.#bubble("bot", "", "");
    const text = reply.querySelector("p")!;
    const time = reply.querySelector("time")!;
    reply.classList.add("is-typing");
    text.append(typingDots());
    thread.append(reply);
    // Only a wait that drags on needs explaining (the hint under the dots).
    const slow = window.setTimeout(() => reply.classList.add("is-slow"), 6000);
    this.#follow(reply, "smooth");

    let answer = "";
    let finished = false;
    const show = (value: string) => {
      if (reply.classList.contains("is-typing")) {
        reply.classList.remove("is-typing");
        text.replaceChildren();
      }
      answer = value;
      text.textContent = answer;
      this.#follow(reply, "instant");
    };

    try {
      const response = await fetch("/panel/chatbot/preguntar", {
        method: "POST",
        headers: { Accept: "text/event-stream" },
        body: new URLSearchParams({ pregunta: question }),
      });
      // The panel password expired: go and enter it again.
      if (response.redirected && new URL(response.url).pathname === "/panel/acceso") {
        location.assign(response.url);
        return;
      }
      const type = response.headers.get("Content-Type") ?? "";
      if (!response.ok || !response.body || !type.startsWith("text/event-stream")) {
        throw new Error(`HTTP ${response.status}`);
      }
      for await (const { event, data } of readEvents(response.body)) {
        const payload = JSON.parse(data) as { t: string; time?: string };
        if (event === "delta") show(answer + payload.t);
        else if (event === "replace") show(payload.t);
        else if (event === "done") {
          show(payload.t);
          time.textContent = payload.time ?? clock();
          finished = true;
        }
      }
      if (!finished) throw new Error("respuesta incompleta");
    } catch {
      show(answer || "No pude enviar tu pregunta. Revisa la conexión e inténtalo de nuevo.");
      time.textContent = clock();
    } finally {
      window.clearTimeout(slow);
      this.#setBusy(false);
    }
  }

  // Keeps the newest bubble in view as it grows. The composer sits right under
  // it, so scrolling to the composer shows both; its scroll margin keeps it
  // clear of the sticky tab bar.
  #follow(reply: HTMLElement, behavior: ScrollBehavior) {
    (this.#form ?? reply).scrollIntoView({ block: "end", behavior });
  }

  #bubble(kind: "user" | "bot", body: string, at: string) {
    const template = this.querySelector<HTMLTemplateElement>(`template[data-template="${kind}"]`)!;
    const node = template.content.firstElementChild!.cloneNode(true) as HTMLElement;
    node.querySelector("p")!.textContent = body;
    node.querySelector("time")!.textContent = at;
    return node;
  }

  // One question at a time, like hx-sync="drop" before: the inputs are
  // disabled until the answer is complete.
  #setBusy(busy: boolean) {
    this.#busy = busy;
    this.toggleAttribute("aria-busy", busy);
    for (const el of this.querySelectorAll<HTMLInputElement | HTMLButtonElement>(
      "form[data-composer] :is(input, button), button[data-ask]",
    )) {
      if (busy) {
        el.dataset["wasDisabled"] = String(el.disabled);
        el.disabled = true;
      } else {
        el.disabled = el.dataset["wasDisabled"] === "true";
        delete el.dataset["wasDisabled"];
      }
    }
    if (!busy) this.#form?.querySelector<HTMLInputElement>("input[name=pregunta]")?.focus();
  }
}

// readEvents parses a text/event-stream body into its events.
async function* readEvents(body: ReadableStream<Uint8Array>) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    // stream: true keeps a multi-byte character split across chunks intact.
    buffer += decoder.decode(value, { stream: true });
    let end: number;
    while ((end = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, end);
      buffer = buffer.slice(end + 2);
      let event = "message";
      const data: string[] = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
      }
      if (data.length) yield { event, data: data.join("\n") };
    }
  }
}

// typingDots is the "escribiendo…" indicator. The hint under it is shown only
// once the bubble gets .is-slow, when the wait starts to need an explanation.
function typingDots() {
  const wrap = document.createElement("span");
  wrap.className = "panel-typing";
  wrap.setAttribute("role", "status");
  const dots = document.createElement("span");
  dots.className = "panel-typing__dots";
  dots.setAttribute("aria-hidden", "true");
  dots.append(document.createElement("i"), document.createElement("i"), document.createElement("i"));
  const hint = document.createElement("span");
  hint.className = "panel-typing__hint";
  hint.textContent = "Buscando en los documentos de la facultad…";
  wrap.append(dots, hint);
  return wrap;
}

function clock() {
  return new Date().toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", hour12: false });
}
